// Auto-extracted for Pass 3: small, reusable UI primitives used by LandingInner

import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";

// Small shared title block used by landing sections
function SectionTitle({ title, subtitle }) {
  return (
    <div className="mb-6">
      <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-emerald-200">
        {title}
      </h2>
      {subtitle ? (
        <p className="mt-2 text-sm md:text-base text-white/70">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

// Shared styling helpers used by multiple blocks in this file
const sectionShell =
  "rounded-3xl border border-cyan-500/40 bg-slate-950/70 backdrop-blur shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_30px_80px_rgba(0,0,0,0.55)] p-5 md:p-6";
const fieldWrap = "flex flex-col gap-1";
const labelCls = "text-xs text-slate-200/80";
const inputCls =
  "w-full rounded-2xl border border-cyan-500/30 bg-slate-950/50 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-500/10";
const selectCls =
  "w-full rounded-2xl border border-cyan-500/30 bg-slate-950/50 px-4 py-3 text-slate-100 outline-none focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-500/10";
const textareaCls =
  "w-full min-h-[120px] rounded-2xl border border-cyan-500/30 bg-slate-950/50 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-500/10";
const helperCls = "text-xs text-slate-300/70";
const btnPrimary =
  "inline-flex items-center justify-center rounded-2xl border border-cyan-400/60 bg-slate-950/60 px-5 py-3 font-semibold text-slate-100 shadow-[0_0_0_1px_rgba(34,211,238,0.15)] hover:border-cyan-300/80 hover:shadow-[0_0_0_1px_rgba(34,211,238,0.22)] transition";
const btnDisabled = "opacity-50 pointer-events-none";


function PaymentsSection({
  t = {},
  paymentDraft,
  setPaymentDraft,
  paymentPlanOptions = [],
  onSubmit,
  status,
}) {
  const plans = paymentPlanOptions?.length
    ? paymentPlanOptions
    : [
        { label: "Youth - $5", value: "Membership — Youth" },
        { label: "Student - $10", value: "Membership — Student" },
      ];

  const frequencyOptions = [
    { label: "One-time", value: "one-time" },
    { label: "Weekly", value: "weekly" },
    { label: "Bi-weekly", value: "biweekly" },
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Yearly", value: "yearly" },
  ];

  const resolveSuggested = () => {
    const opt = plans.find((p) => p.value === paymentDraft.plan || p.value === `tier:${paymentDraft.plan}` || p.value === `package:${paymentDraft.plan}`) ||
      plans.find((p) => p.value === paymentDraft.plan);
    const label = opt?.label || paymentDraft.plan || "";
    const m = label.match(/\$([0-9]+(?:\.[0-9]{1,2})?)/);
    if (m?.[1]) return `$${m[1]}`;
    return paymentDraft.amountPreset || "$50";
  };

  const suggested = resolveSuggested();

  const setField = (k, v) =>
    setPaymentDraft((d) => ({
      ...d,
      [k]: v,
    }));

  const cleanMoney = (v) => String(v ?? "").replace(/[^0-9.]/g, "");
  const hasEmail = String(paymentDraft.email || "").trim().length > 3;
  const amountNum = Number(cleanMoney(paymentDraft.amount));
  const canProceed = hasEmail && Number.isFinite(amountNum) && amountNum >= 5;

  const statusText =
    status === "loading"
      ? "Redirecting to Stripe Checkout…"
      : status?.startsWith("error:")
      ? status.replace("error:", "")
      : "";

  return (
    <section id="payments" className="mt-16">
      <SectionTitle
        title={t?.paymentsProcessing?.heading || "Contribution"}
        subtitle={
          t?.paymentsProcessing?.body ||
          "Choose a contribution frequency and amount. Payment details are entered safely on Stripe Checkout."
        }
      />

      <div className={sectionShell}>
        <div className="grid gap-4 md:grid-cols-2">
<div className={fieldWrap}>
            <label className={labelCls}>Billing Frequency</label>
            <select
              className={selectCls}
              value={paymentDraft.frequency}
              onChange={(e) => setField("frequency", e.target.value)}
            >
              {frequencyOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
<div className={fieldWrap}>
            <label className={labelCls}>Amount</label>
            <input
              className={inputCls}
              value={paymentDraft.amount}
              onChange={(e) => setField("amount", e.target.value)}
              placeholder="$50"
              inputMode="decimal"
            />
          </div>

          <div className={fieldWrap + " md:col-span-2"}>
            <label className={labelCls}>Email</label>
            <input
              className={inputCls}
              value={paymentDraft.email}
              onChange={(e) => setField("email", e.target.value)}
              placeholder="you@email.com"
              inputMode="email"
            />
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            className={btnPrimary + " w-full sm:w-auto"}
            disabled={!canProceed || status === "loading"}
            onClick={() => onSubmit?.()}
          >
            Proceed to Payment
          </button>

          {statusText ? (
            <div className="text-xs text-slate-300">{statusText}</div>
          ) : (
            <div className="text-xs text-slate-300">
              Payment details are entered on Stripe Checkout (not on this site).
            </div>
          )}
        </div>
      </div>
    </section>
  );
}


function CalculatorSection() {
  const [monthly, setMonthly] = useState(100);

  const cause = monthly * 0.5;
  const personal = monthly * 0.5;

  const insurance = personal * 0.25;
  const savings = personal * 0.2;
  const business = personal * 0.25;
  const assets = personal * 0.3;

  const fmt = (n) => Number(n || 0).toFixed(2);

  return (
    <section
      id="calculator"
      className="space-y-4 rounded-3xl border border-sky-500/40 bg-slate-900/70 p-5 sm:p-6"
    >
      <h2 className="text-2xl sm:text-3xl font-semibold text-emerald-300">
        Contribution & Wealth Engine Calculator
      </h2>

      <p className="text-sm sm:text-base text-slate-200 max-w-3xl">
        Visual example of how a monthly contribution may be structured under the
        WOW Wealth Engine model.
      </p>

      <div className="space-y-4">
        <div className="flex items-center justify-between text-sm text-slate-200">
          <span>Monthly contribution (USD)</span>
          <span className="font-semibold text-emerald-300">${monthly}</span>
        </div>

        <input
          type="range"
          min={5}
          max={500}
          step={5}
          value={monthly}
          onChange={(e) => setMonthly(Number(e.target.value))}
          className="w-full"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 mt-4">
        <div className="rounded-2xl border border-emerald-500/40 bg-slate-950/70 p-4">
          <h3 className="text-xs font-semibold text-emerald-300 uppercase tracking-wide mb-1">
            50% — People in Need & Humanitarian Aid
          </h3>
          <div className="text-3xl font-bold text-emerald-300 mb-2">
            ${fmt(cause)}
          </div>
          <p className="text-xs text-slate-300">
            Direct relief, community aid, veterans, seniors, emergencies.
          </p>
        </div>

        <div className="rounded-2xl border border-sky-500/40 bg-slate-950/70 p-4">
          <h3 className="text-xs font-semibold text-sky-300 uppercase tracking-wide mb-1">
            50% — WOW Wealth Engine (Personal Allocation)
          </h3>
          <div className="text-3xl font-bold text-sky-300 mb-2">
            ${fmt(personal)}
          </div>

          <ul className="text-xs text-slate-200 space-y-1">
            <li>• Protected savings & insurance (25%): ${fmt(insurance)}</li>
            <li>• Personal savings (20%): ${fmt(savings)}</li>
            <li>• Business & overhead (25%): ${fmt(business)}</li>
            <li>• Asset ownership (30%): ${fmt(assets)}</li>
          </ul>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 mt-3">
        Conceptual illustration only. Final allocations depend on compliance,
        banking, and legal structure.
      </p>
    </section>
  );
}

/* ---------------- MODAL SHELL ---------------- */
function ModalShell({ title, onClose, children, maxW = "max-w-xl" }) {
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button type="button" onClick={onClose} aria-label="Close" className="modal-mobile-close">×</button>
      <motion.div
        className={`w-full ${maxW} rounded-3xl border border-slate-700 bg-slate-950/90 backdrop-blur p-5 shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 12, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.99 }}
        transition={{ duration: 0.18 }}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-lg font-semibold text-emerald-300">{title}</h3>
          <button
            onClick={onClose}
            className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-200 hover:border-emerald-400"
          >
            Close
          </button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

/* ---------------- PAGE ---------------- */
export { PaymentsSection, CalculatorSection, ModalShell };
